// src/utils/programs/printProgramStyles.js
export const printProgramStyles = `:root {
  /* Primitive Color Tokens */
  --color-white: rgba(255, 255, 255, 1);
  --color-black: rgba(0, 0, 0, 1);
  --color-cream-50: rgba(252, 252, 249, 1);
  --color-cream-100: rgba(255, 255, 253, 1);
  --color-gray-200: rgba(245, 245, 245, 1);
  --color-gray-300: rgba(167, 169, 169, 1);
  --color-gray-400: rgba(119, 124, 124, 1);
  --color-slate-500: rgba(98, 108, 113, 1);
  --color-brown-600: rgba(94, 82, 64, 1);
  --color-charcoal-700: rgba(31, 33, 33, 1);
  --color-charcoal-800: rgba(38, 40, 40, 1);
  --color-slate-900: rgba(19, 52, 59, 1);
  --color-teal-300: rgba(50, 184, 198, 1);
  --color-teal-400: rgba(45, 166, 178, 1);
  --color-teal-500: rgba(33, 128, 141, 1);
  --color-teal-600: rgba(29, 116, 128, 1);
  --color-teal-700: rgba(26, 104, 115, 1);
  --color-teal-800: rgba(41, 150, 161, 1);
  --color-red-400: rgba(255, 84, 89, 1);
  --color-red-500: rgba(192, 21, 47, 1);
  --color-orange-400: rgba(230, 129, 97, 1);
  --color-orange-500: rgba(168, 75, 47, 1);

  /* RGB versions for opacity control */
  --color-brown-600-rgb: 94, 82, 64;
  --color-teal-500-rgb: 33, 128, 141;
  --color-slate-900-rgb: 19, 52, 59;
  --color-slate-500-rgb: 98, 108, 113;
  --color-red-500-rgb: 192, 21, 47;
  --color-red-400-rgb: 255, 84, 89;
  --color-orange-500-rgb: 168, 75, 47;
  --color-orange-400-rgb: 230, 129, 97;

  /* Background color tokens (Light Mode) */
  --color-bg-1: rgba(59, 130, 246, 0.08); /* Light blue */
  --color-bg-2: rgba(245, 158, 11, 0.08); /* Light yellow */
  --color-bg-3: rgba(34, 197, 94, 0.08); /* Light green */
  --color-bg-4: rgba(239, 68, 68, 0.08); /* Light red */
  --color-bg-5: rgba(147, 51, 234, 0.08); /* Light purple */
  --color-bg-6: rgba(249, 115, 22, 0.08); /* Light orange */
  --color-bg-7: rgba(236, 72, 153, 0.08); /* Light pink */
  --color-bg-8: rgba(6, 182, 212, 0.08); /* Light cyan */

  /* Semantic Color Tokens (Light Mode) */
  --color-background: var(--color-cream-50);
  --color-surface: var(--color-cream-100);
  --color-text: var(--color-slate-900);
  --color-text-secondary: var(--color-slate-500);
  --color-primary: var(--color-teal-500);
  --color-primary-hover: var(--color-teal-600);
  --color-primary-active: var(--color-teal-700);
  --color-secondary: rgba(var(--color-brown-600-rgb), 0.12);
  --color-secondary-hover: rgba(var(--color-brown-600-rgb), 0.2);
  --color-secondary-active: rgba(var(--color-brown-600-rgb), 0.25);
  --color-border: rgba(var(--color-brown-600-rgb), 0.2);
  --color-btn-primary-text: var(--color-cream-50);
  --color-card-border: rgba(var(--color-brown-600-rgb), 0.12);
  --color-card-border-inner: rgba(var(--color-brown-600-rgb), 0.12);
  --color-error: var(--color-red-500);
  --color-success: var(--color-teal-500);
  --color-warning: var(--color-orange-500);
  --color-info: var(--color-slate-500);
  --color-focus-ring: rgba(var(--color-teal-500-rgb), 0.4);
  --color-select-caret: rgba(var(--color-slate-900-rgb), 0.8);

  /* Common style patterns */
  --focus-ring: 0 0 0 3px var(--color-focus-ring);
  --focus-outline: 2px solid var(--color-primary);
  --status-bg-opacity: 0.15;
  --status-border-opacity: 0.25;
  --select-caret-light: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23134252' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  --select-caret-dark: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23f5f5f5' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");

  /* RGB versions for opacity control */
  --color-success-rgb: 33, 128, 141;
  --color-error-rgb: 192, 21, 47;
  --color-warning-rgb: 168, 75, 47;
  --color-info-rgb: 98, 108, 113;

  /* Typography */
  --font-family-base: "FKGroteskNeue", "Geist", "Inter", -apple-system,
    BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --font-family-mono: "Berkeley Mono", ui-monospace, SFMono-Regular, Menlo,
    Monaco, Consolas, monospace;
  --font-size-xs: 11px;
  --font-size-sm: 12px;
  --font-size-base: 14px;
  --font-size-md: 14px;
  --font-size-lg: 16px;
  --font-size-xl: 18px;
  --font-size-2xl: 20px;
  --font-size-3xl: 24px;
  --font-size-4xl: 30px;
  --font-weight-normal: 400;
  --font-weight-medium: 500;
  --font-weight-semibold: 550;
  --font-weight-bold: 600;
  --line-height-tight: 1.2;
  --line-height-normal: 1.5;
  --letter-spacing-tight: -0.01em;

  /* Spacing */
  --space-0: 0;
  --space-1: 1px;
  --space-2: 2px;
  --space-4: 4px;
  --space-6: 6px;
  --space-8: 8px;
  --space-10: 10px;
  --space-12: 12px;
  --space-16: 16px;
  --space-20: 20px;
  --space-24: 24px;
  --space-32: 32px;

  /* Border Radius */
  --radius-sm: 6px;
  --radius-base: 8px;
  --radius-md: 10px;
  --radius-lg: 12px;
  --radius-full: 9999px;

  /* Shadows */
  --shadow-xs: 0 1px 2px rgba(0, 0, 0, 0.02);
  --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.04),
    0 2px 4px -1px rgba(0, 0, 0, 0.02);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.04),
    0 4px 6px -2px rgba(0, 0, 0, 0.02);
  --shadow-inset-sm: inset 0 1px 0 rgba(255, 255, 255, 0.15),
    inset 0 -1px 0 rgba(0, 0, 0, 0.03);

  /* Animation */
  --duration-fast: 150ms;
  --duration-normal: 250ms;
  --ease-standard: cubic-bezier(0.16, 1, 0.3, 1);

  /* Layout */
  --container-sm: 640px;
  --container-md: 768px;
  --container-lg: 1024px;
  --container-xl: 1280px;
}

/* Dark mode colors */
@media (prefers-color-scheme: dark) {
  :root {
    /* RGB versions for opacity control (Dark Mode) */
    --color-gray-400-rgb: 119, 124, 124;
    --color-teal-300-rgb: 50, 184, 198;
    --color-gray-300-rgb: 167, 169, 169;
    --color-gray-200-rgb: 245, 245, 245;

    /* Background color tokens (Dark Mode) */
    --color-bg-1: rgba(29, 78, 216, 0.15); /* Dark blue */
    --color-bg-2: rgba(180, 83, 9, 0.15); /* Dark yellow */
    --color-bg-3: rgba(21, 128, 61, 0.15); /* Dark green */
    --color-bg-4: rgba(185, 28, 28, 0.15); /* Dark red */
    --color-bg-5: rgba(107, 33, 168, 0.15); /* Dark purple */
    --color-bg-6: rgba(194, 65, 12, 0.15); /* Dark orange */
    --color-bg-7: rgba(190, 24, 93, 0.15); /* Dark pink */
    --color-bg-8: rgba(8, 145, 178, 0.15); /* Dark cyan */

    /* Semantic Color Tokens (Dark Mode) */
    --color-background: var(--color-charcoal-700);
    --color-surface: var(--color-charcoal-800);
    --color-text: var(--color-gray-200);
    --color-text-secondary: rgba(var(--color-gray-300-rgb), 0.7);
    --color-primary: var(--color-teal-300);
    --color-primary-hover: var(--color-teal-400);
    --color-primary-active: var(--color-teal-800);
    --color-secondary: rgba(var(--color-gray-400-rgb), 0.15);
    --color-secondary-hover: rgba(var(--color-gray-400-rgb), 0.25);
    --color-secondary-active: rgba(var(--color-gray-400-rgb), 0.3);
    --color-border: rgba(var(--color-gray-400-rgb), 0.3);
    --color-error: var(--color-red-400);
    --color-success: var(--color-teal-300);
    --color-warning: var(--color-orange-400);
    --color-info: var(--color-gray-300);
    --color-focus-ring: rgba(var(--color-teal-300-rgb), 0.4);
    --color-btn-primary-text: var(--color-slate-900);
    --color-card-border: rgba(var(--color-gray-400-rgb), 0.2);
    --color-card-border-inner: rgba(var(--color-gray-400-rgb), 0.15);
    --shadow-inset-sm: inset 0 1px 0 rgba(255, 255, 255, 0.1),
      inset 0 -1px 0 rgba(0, 0, 0, 0.15);
    --button-border-secondary: rgba(var(--color-gray-400-rgb), 0.2);
    --color-border-secondary: rgba(var(--color-gray-400-rgb), 0.2);
    --color-select-caret: rgba(var(--color-gray-200-rgb), 0.8);

    /* Common style patterns - updated for dark mode */
    --focus-ring: 0 0 0 3px var(--color-focus-ring);
    --focus-outline: 2px solid var(--color-primary);
    --status-bg-opacity: 0.15;
    --status-border-opacity: 0.25;
    --select-caret-light: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23134252' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    --select-caret-dark: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23f5f5f5' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");

    /* RGB versions for dark mode */
    --color-success-rgb: var(--color-teal-300-rgb);
    --color-error-rgb: var(--color-red-400-rgb);
    --color-warning-rgb: var(--color-orange-400-rgb);
    --color-info-rgb: var(--color-gray-300-rgb);
  }
}

/* Data attribute for manual theme switching */
[data-color-scheme="dark"] {
  /* RGB versions for opacity control (dark mode) */
  --color-gray-400-rgb: 119, 124, 124;
  --color-teal-300-rgb: 50, 184, 198;
  --color-gray-300-rgb: 167, 169, 169;
  --color-gray-200-rgb: 245, 245, 245;

  /* Colorful background palette - Dark Mode */
  --color-bg-1: rgba(29, 78, 216, 0.15); /* Dark blue */
  --color-bg-2: rgba(180, 83, 9, 0.15); /* Dark yellow */
  --color-bg-3: rgba(21, 128, 61, 0.15); /* Dark green */
  --color-bg-4: rgba(185, 28, 28, 0.15); /* Dark red */
  --color-bg-5: rgba(107, 33, 168, 0.15); /* Dark purple */
  --color-bg-6: rgba(194, 65, 12, 0.15); /* Dark orange */
  --color-bg-7: rgba(190, 24, 93, 0.15); /* Dark pink */
  --color-bg-8: rgba(8, 145, 178, 0.15); /* Dark cyan */

  /* Semantic Color Tokens (Dark Mode) */
  --color-background: var(--color-charcoal-700);
  --color-surface: var(--color-charcoal-800);
  --color-text: var(--color-gray-200);
  --color-text-secondary: rgba(var(--color-gray-300-rgb), 0.7);
  --color-primary: var(--color-teal-300);
  --color-primary-hover: var(--color-teal-400);
  --color-primary-active: var(--color-teal-800);
  --color-secondary: rgba(var(--color-gray-400-rgb), 0.15);
  --color-secondary-hover: rgba(var(--color-gray-400-rgb), 0.25);
  --color-secondary-active: rgba(var(--color-gray-400-rgb), 0.3);
  --color-border: rgba(var(--color-gray-400-rgb), 0.3);
  --color-error: var(--color-red-400);
  --color-success: var(--color-teal-300);
  --color-warning: var(--color-orange-400);
  --color-info: var(--color-gray-300);
  --color-focus-ring: rgba(var(--color-teal-300-rgb), 0.4);
  --color-btn-primary-text: var(--color-slate-900);
  --color-card-border: rgba(var(--color-gray-400-rgb), 0.15);
  --color-card-border-inner: rgba(var(--color-gray-400-rgb), 0.15);
  --shadow-inset-sm: inset 0 1px 0 rgba(255, 255, 255, 0.1),
    inset 0 -1px 0 rgba(0, 0, 0, 0.15);
  --color-border-secondary: rgba(var(--color-gray-400-rgb), 0.2);
  --color-select-caret: rgba(var(--color-gray-200-rgb), 0.8);

  /* Common style patterns - updated for dark mode */
  --focus-ring: 0 0 0 3px var(--color-focus-ring);
  --focus-outline: 2px solid var(--color-primary);
  --status-bg-opacity: 0.15;
  --status-border-opacity: 0.25;
  --select-caret-light: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23134252' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  --select-caret-dark: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23f5f5f5' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");

  /* RGB versions for dark mode */
  --color-success-rgb: var(--color-teal-300-rgb);
  --color-error-rgb: var(--color-red-400-rgb);
  --color-warning-rgb: var(--color-orange-400-rgb);
  --color-info-rgb: var(--color-gray-300-rgb);
}

[data-color-scheme="light"] {
  /* RGB versions for opacity control (light mode) */
  --color-brown-600-rgb: 94, 82, 64;
  --color-teal-500-rgb: 33, 128, 141;
  --color-slate-900-rgb: 19, 52, 59;

  /* Semantic Color Tokens (Light Mode) */
  --color-background: var(--color-cream-50);
  --color-surface: var(--color-cream-100);
  --color-text: var(--color-slate-900);
  --color-text-secondary: var(--color-slate-500);
  --color-primary: var(--color-teal-500);
  --color-primary-hover: var(--color-teal-600);
  --color-primary-active: var(--color-teal-700);
  --color-secondary: rgba(var(--color-brown-600-rgb), 0.12);
  --color-secondary-hover: rgba(var(--color-brown-600-rgb), 0.2);
  --color-secondary-active: rgba(var(--color-brown-600-rgb), 0.25);
  --color-border: rgba(var(--color-brown-600-rgb), 0.2);
  --color-btn-primary-text: var(--color-cream-50);
  --color-card-border: rgba(var(--color-brown-600-rgb), 0.12);
  --color-card-border-inner: rgba(var(--color-brown-600-rgb), 0.12);
  --color-error: var(--color-red-500);
  --color-success: var(--color-teal-500);
  --color-warning: var(--color-orange-500);
  --color-info: var(--color-slate-500);
  --color-focus-ring: rgba(var(--color-teal-500-rgb), 0.4);

  /* RGB versions for light mode */
  --color-success-rgb: var(--color-teal-500-rgb);
  --color-error-rgb: var(--color-red-500-rgb);
  --color-warning-rgb: var(--color-orange-500-rgb);
  --color-info-rgb: var(--color-slate-500-rgb);
}

/* Base styles */
html {
  font-size: var(--font-size-base);
  font-family: var(--font-family-base);
  line-height: var(--line-height-normal);
  color: var(--color-text);
  background-color: var(--color-background);
  -webkit-font-smoothing: antialiased;
  box-sizing: border-box;
}

body {
  margin: 0;
  padding: 0;
}

*,
*::before,
*::after {
  box-sizing: inherit;
}

/* Typography */
h1,
h2,
h3,
h4,
h5,
h6 {
  margin: 0;
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-tight);
  color: var(--color-text);
  letter-spacing: var(--letter-spacing-tight);
}

h1 {
  font-size: var(--font-size-4xl);
}
h2 {
  font-size: var(--font-size-3xl);
}
h3 {
  font-size: var(--font-size-2xl);
}
h4 {
  font-size: var(--font-size-xl);
}
h5 {
  font-size: var(--font-size-lg);
}
h6 {
  font-size: var(--font-size-md);
}

p {
  margin: 0 0 var(--space-16) 0;
}

a {
  color: var(--color-primary);
  text-decoration: none;
  transition: color var(--duration-fast) var(--ease-standard);
}

a:hover {
  color: var(--color-primary-hover);
}

code,
pre {
  font-family: var(--font-family-mono);
  font-size: calc(var(--font-size-base) * 0.95);
  background-color: var(--color-secondary);
  border-radius: var(--radius-sm);
}

code {
  padding: var(--space-1) var(--space-4);
}

pre {
  padding: var(--space-16);
  margin: var(--space-16) 0;
  overflow: auto;
  border: 1px solid var(--color-border);
}

pre code {
  background: none;
  padding: 0;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-8) var(--space-16);
  border-radius: var(--radius-base);
  font-size: var(--font-size-base);
  font-weight: 500;
  line-height: 1.5;
  cursor: pointer;
  transition: all var(--duration-normal) var(--ease-standard);
  border: none;
  text-decoration: none;
  position: relative;
}

.btn:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

.btn--primary {
  background: var(--color-primary);
  color: var(--color-btn-primary-text);
}

.btn--primary:hover {
  background: var(--color-primary-hover);
}

.btn--primary:active {
  background: var(--color-primary-active);
}

.btn--secondary {
  background: var(--color-secondary);
  color: var(--color-text);
}

.btn--secondary:hover {
  background: var(--color-secondary-hover);
}

.btn--secondary:active {
  background: var(--color-secondary-active);
}

.btn--outline {
  background: transparent;
  border: 1px solid var(--color-border);
  color: var(--color-text);
}

.btn--outline:hover {
  background: var(--color-secondary);
}

.btn--sm {
  padding: var(--space-4) var(--space-12);
  font-size: var(--font-size-sm);
  border-radius: var(--radius-sm);
}

.btn--lg {
  padding: var(--space-10) var(--space-20);
  font-size: var(--font-size-lg);
  border-radius: var(--radius-md);
}

.btn--full-width {
  width: 100%;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Form elements */
.form-control {
  display: block;
  width: 100%;
  padding: var(--space-8) var(--space-12);
  font-size: var(--font-size-md);
  line-height: 1.5;
  color: var(--color-text);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-base);
  transition: border-color var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard);
}

textarea.form-control {
  font-family: var(--font-family-base);
  font-size: var(--font-size-base);
}

select.form-control {
  padding: var(--space-8) var(--space-12);
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: var(--select-caret-light);
  background-repeat: no-repeat;
  background-position: right var(--space-12) center;
  background-size: 16px;
  padding-right: var(--space-32);
}

/* Add a dark mode specific caret */
@media (prefers-color-scheme: dark) {
  select.form-control {
    background-image: var(--select-caret-dark);
  }
}

/* Also handle data-color-scheme */
[data-color-scheme="dark"] select.form-control {
  background-image: var(--select-caret-dark);
}

[data-color-scheme="light"] select.form-control {
  background-image: var(--select-caret-light);
}

.form-control:focus {
  border-color: var(--color-primary);
  outline: var(--focus-outline);
}

.form-label {
  display: block;
  margin-bottom: var(--space-8);
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-sm);
}

.form-group {
  margin-bottom: var(--space-16);
}

/* Card component */
.card {
  background-color: var(--color-surface);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-card-border);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  transition: box-shadow var(--duration-normal) var(--ease-standard);
}

.card:hover {
  box-shadow: var(--shadow-md);
}

.card__body {
  padding: var(--space-16);
}

.card__header,
.card__footer {
  padding: var(--space-16);
  border-bottom: 1px solid var(--color-card-border-inner);
}

/* Status indicators - simplified with CSS variables */
.status {
  display: inline-flex;
  align-items: center;
  padding: var(--space-6) var(--space-12);
  border-radius: var(--radius-full);
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-sm);
}

.status--success {
  background-color: rgba(
    var(--color-success-rgb, 33, 128, 141),
    var(--status-bg-opacity)
  );
  color: var(--color-success);
  border: 1px solid
    rgba(var(--color-success-rgb, 33, 128, 141), var(--status-border-opacity));
}

.status--error {
  background-color: rgba(
    var(--color-error-rgb, 192, 21, 47),
    var(--status-bg-opacity)
  );
  color: var(--color-error);
  border: 1px solid
    rgba(var(--color-error-rgb, 192, 21, 47), var(--status-border-opacity));
}

.status--warning {
  background-color: rgba(
    var(--color-warning-rgb, 168, 75, 47),
    var(--status-bg-opacity)
  );
  color: var(--color-warning);
  border: 1px solid
    rgba(var(--color-warning-rgb, 168, 75, 47), var(--status-border-opacity));
}

.status--info {
  background-color: rgba(
    var(--color-info-rgb, 98, 108, 113),
    var(--status-bg-opacity)
  );
  color: var(--color-info);
  border: 1px solid
    rgba(var(--color-info-rgb, 98, 108, 113), var(--status-border-opacity));
}

/* Container layout */
.container {
  width: 100%;
  margin-right: auto;
  margin-left: auto;
  padding-right: var(--space-16);
  padding-left: var(--space-16);
}

@media (min-width: 640px) {
  .container {
    max-width: var(--container-sm);
  }
}
@media (min-width: 768px) {
  .container {
    max-width: var(--container-md);
  }
}
@media (min-width: 1024px) {
  .container {
    max-width: var(--container-lg);
  }
}
@media (min-width: 1280px) {
  .container {
    max-width: var(--container-xl);
  }
}

/* Utility classes */
.flex {
  display: flex;
}
.flex-col {
  flex-direction: column;
}
.items-center {
  align-items: center;
}
.justify-center {
  justify-content: center;
}
.justify-between {
  justify-content: space-between;
}
.gap-4 {
  gap: var(--space-4);
}
.gap-8 {
  gap: var(--space-8);
}
.gap-16 {
  gap: var(--space-16);
}

.m-0 {
  margin: 0;
}
.mt-8 {
  margin-top: var(--space-8);
}
.mb-8 {
  margin-bottom: var(--space-8);
}
.mx-8 {
  margin-left: var(--space-8);
  margin-right: var(--space-8);
}
.my-8 {
  margin-top: var(--space-8);
  margin-bottom: var(--space-8);
}

.p-0 {
  padding: 0;
}
.py-8 {
  padding-top: var(--space-8);
  padding-bottom: var(--space-8);
}
.px-8 {
  padding-left: var(--space-8);
  padding-right: var(--space-8);
}
.py-16 {
  padding-top: var(--space-16);
  padding-bottom: var(--space-16);
}
.px-16 {
  padding-left: var(--space-16);
  padding-right: var(--space-16);
}

.block {
  display: block;
}
.hidden {
  display: none;
}

/* Accessibility */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

:focus-visible {
  outline: var(--focus-outline);
  outline-offset: 2px;
}

/* Dark mode specifics */
[data-color-scheme="dark"] .btn--outline {
  border: 1px solid var(--color-border-secondary);
}

@font-face {
  font-family: 'FKGroteskNeue';
  src: url('https://r2cdn.perplexity.ai/fonts/FKGroteskNeue.woff2')
    format('woff2');
}

/* END PERPLEXITY DESIGN SYSTEM */
/* ========================================
   🖨️ PRINT PROGRAM STYLES - PROFESSIONAL
   ======================================== */
/* ==================== MODERN COVER PAGE - PROFESSIONAL ==================== */
/* ==================== MODERN LAYERED COVER PAGE - PROFESSIONAL ==================== */

.print-cover-page-modern {
  position: relative;
  width: 100%;
  height: 100vh;
  page-break-after: always;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  margin: 0;
  padding: 0;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
}

/* ===== Background Image ===== */
.cover-background-img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  z-index: 1;
  opacity: 0.4;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

/* ===== Multi-Layer Gradient Overlay ===== */
.cover-gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background: 
    radial-gradient(circle at 20% 80%, rgba(33, 128, 141, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(41, 150, 161, 0.12) 0%, transparent 50%),
    linear-gradient(135deg, 
      rgba(19, 52, 59, 0.35) 0%, 
      rgba(26, 104, 115, 0.28) 50%,
      rgba(33, 128, 141, 0.32) 100%);
  z-index: 2;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}


/* ===== Main Content Container ===== */
.cover-content {
  position: relative;
  z-index: 3;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 50px 40px;
  box-sizing: border-box;
}

/* ===== Top Section - Logo ===== */
.cover-top {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.cover-logo-container {
  position: relative;
}

.logo-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px 40px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.logo-text {
  font-size: 32px;
  font-weight: 900;
  color: white;
  text-transform: uppercase;
  letter-spacing: 3px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.logo-tagline {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 2px;
  font-weight: 500;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

/* ===== Middle Section - Title ===== */
.cover-middle {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 40px;
  width: 100%;
  max-width: 900px;
}

.title-container {
  position: relative;
  padding: 30px 0;
}

.decorative-line {
  height: 3px;
  background: linear-gradient(90deg, 
    transparent 0%, 
    rgba(255, 255, 255, 0.8) 20%, 
    white 50%, 
    rgba(255, 255, 255, 0.8) 80%, 
    transparent 100%);
  margin: 0 auto;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.decorative-line.top {
  width: 200px;
  margin-bottom: 25px;
}

.decorative-line.bottom {
  width: 250px;
  margin-top: 25px;
}

.cover-program-title {
  font-size: 28px;
  font-weight: 700;
  color: white;
  margin: 0;
  text-shadow: 
    0 2px 10px rgba(0, 0, 0, 0.5),
    0 0 40px rgba(50, 184, 198, 0.3);
  letter-spacing: -0.5px;
  line-height: 1.2;
  max-width: 100%;
  width: 100%;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
  white-space: nowrap;
  overflow: visible;
  text-overflow: clip;
  transform-origin: center;
}



/* ===== Floating Info Cards ===== */
.floating-info-cards {
  display: flex;
  gap: 25px;
  justify-content: center;
  flex-wrap: wrap;
  width: 100%;
}

.info-card-modern {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 18px 28px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 2px solid rgba(255, 255, 255, 0.25);
  border-radius: 16px;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.card-icon {
  font-size: 32px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 1.5px;
  font-weight: 600;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.card-value {
  font-size: 18px;
  color: white;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

/* ===== Bottom Section ===== */
.cover-bottom {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
}

.date-range-container {
  display: flex;
  align-items: center;
  gap: 30px;
  padding: 20px 40px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.date-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.date-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 1.5px;
  font-weight: 600;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.date-value {
  font-size: 16px;
  color: white;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.date-separator {
  font-size: 24px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 300;
}

.program-number-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 30px;
  background: linear-gradient(135deg, rgba(50, 184, 198, 0.9) 0%, rgba(33, 128, 141, 0.9) 100%);
  border-radius: 30px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 20px rgba(50, 184, 198, 0.4);
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.badge-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.badge-number {
  font-size: 18px;
  color: white;
  font-weight: 900;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

/* ===== Print Styles ===== */
@media print {
  .print-cover-page-modern {
    height: 100vh;
    page-break-after: always;
    margin: 0 !important;
    padding: 0 !important;
  }

  .cover-background-img,
  .cover-gradient-overlay,
  .logo-badge,
  .info-card-modern,
  .date-range-container,
  .program-number-badge {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .cover-program-title {
    font-size: 56px;
  }
}

/* ===== RTL Support ===== */
.print-document[dir="rtl"] .floating-info-cards,
.print-document[dir="rtl"] .date-range-container {
  direction: rtl;
}

.print-document[dir="rtl"] .date-separator {
  transform: rotate(180deg);
}

.print-document[dir="rtl"] .logo-badge,
.print-document[dir="rtl"] .info-card-modern,
.print-document[dir="rtl"] .program-number-badge {
  direction: rtl;
}

/* ===== Responsive Adjustments ===== */
@media (max-width: 800px) {
  .cover-program-title {
    font-size: 48px;
  }

  .floating-info-cards {
    gap: 15px;
  }

  .info-card-modern {
    padding: 14px 20px;
  }

  .date-range-container {
    padding: 16px 30px;
    gap: 20px;
  }
}


/* ==================== Base Document ==================== */

.print-document {
  width: 210mm;
  margin: 0 auto;
  padding: 0;
  background: var(--color-surface);
  font-family: var(--font-family-base);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  color: var(--color-text);
}

.print-document[dir="rtl"] {
  direction: rtl;
  text-align: right;
}

/* ==================== Header ==================== */

.print-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, var(--color-teal-600) 0%, var(--color-teal-400) 100%);
  color: var(--color-cream-50);
  padding: var(--space-12) var(--space-16);
  border-bottom: 3px solid var(--color-teal-700);
  margin-bottom: var(--space-12);
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.company-name {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.company-tagline {
  font-size: var(--font-size-xs);
  margin: 0;
  opacity: 0.95;
}

.header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--space-4);
}

.program-number {
  background: var(--color-cream-50);
  color: var(--color-teal-600);
  padding: var(--space-4) var(--space-10);
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-sm);
}

.print-date {
  font-size: var(--font-size-xs);
  opacity: 0.9;
}

/* ==================== Program Title ==================== */

.program-title {
  text-align: center;
  margin-bottom: var(--space-12);
  padding: var(--space-8) var(--space-12);
  border-bottom: 2px solid var(--color-secondary);
  page-break-after: avoid;
}

.program-title h2 {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-teal-700);
  margin: 0 0 var(--space-4) 0;
  text-transform: uppercase;
  letter-spacing: var(--letter-spacing-tight);
}

.program-desc {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  font-style: italic;
  margin: 0;
}

/* ==================== Section Title ==================== */

.section-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-cream-50);
  background: linear-gradient(135deg, var(--color-teal-600) 0%, var(--color-teal-400) 100%);
  padding: var(--space-6) var(--space-12);
  margin: var(--space-12) var(--space-12) var(--space-6) var(--space-12);
  border-radius: var(--radius-sm);
  page-break-after: avoid !important;
}

/* ✅ Client & Agent Information Section */

.client-agent-section {
  margin: 0 var(--space-12) var(--space-12) var(--space-12);
  page-break-inside: avoid !important;
}

.info-cards-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-top: var(--space-6);
}

@media (max-width: 800px) {
  .info-cards-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }
}

@media (max-width: 480px) {
  .info-cards-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }
}

.info-card {
  border: 2px solid var(--color-teal-600);
  border-radius: var(--radius-base);
  overflow: hidden;
  background: var(--color-surface);
  page-break-inside: avoid !important;
  box-shadow: var(--shadow-sm);
}

.info-card .card-header {
  background: linear-gradient(135deg, var(--color-teal-600) 0%, var(--color-teal-400) 100%);
  color: var(--color-cream-50);
  padding: var(--space-6) var(--space-10);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-sm);
}

.agent-card .card-header {
  background: linear-gradient(135deg, var(--color-teal-700) 0%, var(--color-teal-500) 100%);
}

.info-card .card-body {
  padding: var(--space-8) var(--space-10);
  background: var(--color-secondary);
}

.info-row {
  display: grid;
  grid-template-columns: 40% 60%;
  padding: var(--space-4) 0;
  border-bottom: 1px solid var(--color-border);
  font-size: var(--font-size-xs);
  gap: var(--space-8);
}

.print-document[dir="rtl"] .info-row {
  grid-template-columns: 60% 40%;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
}

.info-value {
  color: var(--color-text);
  font-weight: var(--font-weight-medium);
  word-wrap: break-word;
}

.print-document[dir="rtl"] .info-value {
  text-align: right;
}

/* ==================== Overview Section ==================== */

.overview-section {
  background: var(--color-secondary);
  border: 2px solid var(--color-teal-600);
  border-radius: var(--radius-base);
  padding: var(--space-10);
  margin: 0 var(--space-12) var(--space-12) var(--space-12);
  page-break-inside: avoid !important;
}

.overview-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-10) var(--space-12);
  margin-bottom: var(--space-8);
}

@media (max-width: 800px) {
  .overview-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .overview-grid {
    grid-template-columns: 1fr;
    gap: var(--space-8);
  }
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-6);
  background: var(--color-surface);
  border-radius: var(--radius-sm);
  border-left: 3px solid var(--color-teal-600);
  box-shadow: var(--shadow-xs);
}

.print-document[dir="rtl"] .info-item {
  border-right: 3px solid var(--color-teal-600);
  border-left: none;
}

.info-item .label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: var(--letter-spacing-tight);
}

.info-item .value {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
}

/* Travelers Table */

.travelers-table {
  margin-top: var(--space-8);
}

.travelers-table table,
.day-products-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-xs);
}

.travelers-table th,
.day-products-table th {
  background: var(--color-teal-600);
  color: var(--color-cream-50);
  padding: var(--space-6);
  font-weight: var(--font-weight-bold);
  border: 1px solid var(--color-teal-700);
  font-size: var(--font-size-xs);
}

.travelers-table td,
.day-products-table td {
  padding: var(--space-4) var(--space-6);
  border: 1px solid var(--color-border);
  text-align: center;
}

.travelers-table tbody tr:nth-child(even),
.day-products-table tbody tr:nth-child(even) {
  background: var(--color-secondary);
}

/* ==================== Itinerary Section ==================== */

.itinerary-section {
  margin: var(--space-12) 0;
}

.day-card {
  border: 2px solid var(--color-teal-600);
  border-radius: var(--radius-base);
  margin: 0 var(--space-12) var(--space-12) var(--space-12);
  overflow: visible;
  page-break-inside: avoid;
  box-shadow: var(--shadow-sm);
}

.day-card.large-day {
  page-break-inside: auto;
}

.day-header {
  background: linear-gradient(135deg, var(--color-teal-600) 0%, var(--color-teal-400) 100%);
  color: var(--color-cream-50);
  padding: var(--space-8) var(--space-12);
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-sm);
  page-break-after: avoid !important;
}

.print-document[dir="rtl"] .day-header {
  flex-direction: row-reverse;
}

.day-number {
  font-weight: var(--font-weight-bold);
}

.day-date {
  font-size: var(--font-size-xs);
  opacity: 0.95;
}

.day-content {
  padding: var(--space-10);
  page-break-inside: auto;
}

.day-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-teal-700);
  margin: 0 0 var(--space-6) 0;
  page-break-after: avoid !important;
}

.day-description {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  margin: 0 0 var(--space-8) 0;
  line-height: var(--line-height-normal);
}

/* Activities */

.activities {
  margin-top: var(--space-8);
}

.activities h5 {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-teal-600);
  margin: 0 0 var(--space-6) 0;
  text-transform: uppercase;
  letter-spacing: var(--letter-spacing-tight);
  page-break-after: avoid !important;
}

.day-products-table {
  page-break-inside: auto;
}

.day-products-table thead {
  display: table-header-group;
}

.day-products-table tbody {
  display: table-row-group;
}

.day-products-table tr {
  page-break-inside: avoid;
}

.cell-time {
  font-family: var(--font-family-mono);
  font-weight: var(--font-weight-semibold);
  text-align: center;
}

.cell-service {
  text-align: left;
}

.print-document[dir="rtl"] .cell-service {
  text-align: right;
}

.product-summary {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  font-style: italic;
  margin-top: var(--space-2);
  line-height: 1.3;
}

/* ==================== PDF Custom Sections ==================== */

.custom-sections {
  margin: var(--space-12) 0;
}

.pdf-custom-section {
  margin: 0 var(--space-12) var(--space-12) var(--space-12);
  border: 2px solid var(--color-success);
  border-radius: var(--radius-base);
  overflow: hidden;
  page-break-inside: avoid !important;
  box-shadow: var(--shadow-sm);
}

.pdf-custom-section .section-title {
  background: linear-gradient(135deg, var(--color-success) 0%, var(--color-primary-hover) 100%);
  color: var(--color-cream-50);
  margin: 0;
  border-radius: 0;
}

.section-items-list {
  list-style-position: inside;
  padding: var(--space-10) var(--space-12);
  margin: 0;
  background: rgba(var(--color-success-rgb), 0.08);
}

.section-items-list li {
  padding: var(--space-4) 0;
  font-size: var(--font-size-xs);
  color: var(--color-text);
  line-height: var(--line-height-normal);
  border-bottom: 1px solid var(--color-border);
}

.section-items-list li:last-child {
  border-bottom: none;
}

.section-note {
  margin: 0;
  padding: var(--space-6) var(--space-12);
  background: rgba(var(--color-warning-rgb), 0.15);
  border-top: 1px solid var(--color-warning);
  font-size: var(--font-size-xs);
  color: var(--color-warning);
  font-style: italic;
  page-break-inside: avoid;
}

/* ==================== Pricing Section ==================== */

.pricing-section {
  background: rgba(var(--color-success-rgb), 0.08);
  border: 3px solid var(--color-success);
  border-radius: var(--radius-base);
  padding: var(--space-12);
  margin: var(--space-12) var(--space-12) var(--space-16) var(--space-12);
  page-break-inside: avoid !important;
  page-break-before: auto;
  box-shadow: var(--shadow-sm);
}

.pricing-section h3 {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-success);
  margin: 0 0 var(--space-8) 0;
  letter-spacing: var(--letter-spacing-tight);
}

.total-price {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-success);
  // padding-top: var(--space-8);
  // border-top: 2px solid var(--color-success);
}

.print-document[dir="rtl"] .total-price {
  flex-direction: row-reverse;
}

.price-amount {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-success);
}

/* ==================== Footer ==================== */

.print-footer {
  margin: var(--space-16) var(--space-12) 0 var(--space-12);
  padding-top: var(--space-8);
  border-top: 2px solid var(--color-border);
  text-align: center;
  page-break-inside: avoid;
}

.print-footer-text {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.3;
}

/* ==================== Print Media Queries ==================== */

@media print {
  @page {
    size: A4 portrait;
    margin: 10mm 8mm;
  }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  html, body {
    margin: 0;
    padding: 0;
    background: var(--color-surface);
    font-size: var(--font-size-sm);
  }
  .info-cards-grid {
    display: grid !important;
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 10px !important;
  }
  .print-document {
    width: 100%;
    margin: 0;
    padding: 0;
  }

  * {
    orphans: 2 !important;
    widows: 2 !important;
  }

  h1, h2, h3, h4, h5, h6 {
    page-break-after: avoid !important;
  }

  .overview-section,
  .client-agent-section {
    page-break-inside: avoid !important;
    page-break-after: auto;
  }

  .day-card {
    page-break-before: auto !important;
    page-break-inside: avoid !important;
  }

  .day-card.large-day {
    page-break-inside: auto !important;
  }

  .day-header {
    page-break-after: avoid !important;
  }

  .day-content {
    page-break-inside: auto;
  }

  .day-title,
  .activities h5 {
    page-break-after: avoid !important;
  }

  .day-products-table {
    page-break-inside: auto !important;
  }

  .day-products-table thead {
    display: table-header-group !important;
  }

  .day-products-table tr {
    page-break-inside: avoid !important;
  }

  .pdf-custom-section {
    page-break-inside: avoid !important;
  }

  .pricing-section {
    page-break-inside: avoid !important;
    page-break-before: auto;
  }

  .print-footer {
    page-break-inside: avoid !important;
  }

  * {
    box-shadow: none !important;
    text-shadow: none !important;
  }
}

/* ==================== RTL Support ==================== */

.print-document[dir="rtl"] {
  direction: rtl;
  text-align: right;
}

.print-document[dir="rtl"] .overview-grid {
  direction: rtl;
}

.print-document[dir="rtl"] .day-header {
  direction: rtl;
}

.print-document[dir="rtl"] .total-price {
  direction: rtl;
}

.print-document[dir="rtl"] .print-footer {
  direction: rtl;
}

.print-document[dir="rtl"] .info-cards-grid {
  direction: rtl;
}

.print-document[dir="rtl"] .info-row {
  direction: rtl;
}

.print-document[dir="rtl"] .program-title {
  direction: rtl;
}

.print-document[dir="rtl"] table {
  direction: rtl;
}

.print-document[dir="rtl"] table th,
.print-document[dir="rtl"] table td {
  text-align: center;
}

/* Responsive Styling */



/* Mobile Portrait - 480px breakpoint */
@media screen and (max-width: 480px) {
  .print-document {
    font-size: var(--font-size-xs);
    padding: var(--space-8);
  }

  .print-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-8);
    padding: var(--space-10);
  }

  .header-right {
    align-items: flex-start;
    width: 100%;
  }

  .company-name {
    font-size: var(--font-size-lg);
  }

  .company-tagline {
    font-size: 10px;
  }

  .program-title h2 {
    font-size: var(--font-size-lg);
  }

  .program-desc {
    font-size: 10px;
  }

  .section-title {
    font-size: var(--font-size-sm);
    padding: var(--space-6) var(--space-8);
    margin: var(--space-8) var(--space-8) var(--space-4) var(--space-8);
  }

  .client-agent-section,
  .overview-section,
  .day-card,
  .pdf-custom-section,
  .pricing-section {
    margin-left: var(--space-8);
    margin-right: var(--space-8);
  }

.info-cards-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-top: var(--space-6);
}

  .info-card .card-body {
    padding: var(--space-6) var(--space-8);
  }

  .info-row {
    grid-template-columns: 35% 65%;
    gap: var(--space-6);
    font-size: 10px;
  }

  .overview-section {
    padding: var(--space-8);
  }

  .overview-grid {
    grid-template-columns: 1fr;
    gap: var(--space-6);
  }

  .info-item {
    padding: var(--space-4);
  }

  .info-item .label {
    font-size: 10px;
  }

  .info-item .value {
    font-size: var(--font-size-xs);
  }

  .travelers-table table,
  .day-products-table {
    font-size: 10px;
  }

  .travelers-table th,
  .day-products-table th {
    padding: var(--space-4);
    font-size: 10px;
  }

  .travelers-table td,
  .day-products-table td {
    padding: var(--space-2) var(--space-4);
  }

  .day-header {
    padding: var(--space-6) var(--space-8);
    font-size: var(--font-size-xs);
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-2);
  }

  .day-content {
    padding: var(--space-8);
  }

  .day-title {
    font-size: var(--font-size-xs);
  }

  .day-description {
    font-size: 10px;
  }

  .activities h5 {
    font-size: 10px;
  }

  .product-summary {
    font-size: 9px;
  }

  .section-items-list {
    padding: var(--space-8);
  }

  .section-items-list li {
    font-size: 10px;
  }

  .section-note {
    padding: var(--space-4) var(--space-8);
    font-size: 10px;
  }

  .pricing-section {
    padding: var(--space-10);
  }

  .pricing-section h3 {
    font-size: var(--font-size-sm);
  }

  .total-price {
    font-size: var(--font-size-md);
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .price-amount {
    font-size: var(--font-size-lg);
  }

  .print-footer {
    margin: var(--space-12) var(--space-8) 0 var(--space-8);
  }

  .print-footer-text {
    font-size: 9px;
  }
}

/* Accessibility Enhancements */

@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* ==================== FLIGHT CONNECTIONS & HOTEL ACCOMMODATION ==================== */
.flight-connections-section,
.hotel-accommodation-section {
  margin: 15px 12px;
  page-break-inside: avoid;
  break-inside: avoid;
}

.flight-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin-top: 10px;
}

.flight-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 15px;
  background: #fff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 120px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.flight-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.flight-card-date {
  font-size: 8pt;
  color: #888;
  font-weight: 600;
  text-transform: uppercase;
}

.flight-card-airline {
  font-weight: 700;
  font-size: 9pt;
  color: #1976d2;
}

.flight-card-route {
  font-weight: 800;
  font-size: 11pt;
  color: #333;
  margin-bottom: 10px;
}

.flight-card-times {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  position: relative;
}

.time-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 30%;
}

.time-val {
  font-size: 14pt;
  font-weight: 800;
  color: #ff9800;
}

.airport-code {
  font-size: 8pt;
  font-weight: 700;
  color: #666;
}

.flight-line-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  position: relative;
  padding: 0 10px;
}

.flight-line {
  width: 100%;
  height: 1px;
  background: #ddd;
  position: relative;
}

.flight-line::after {
  content: '→';
  position: absolute;
  right: 0;
  top: -7px;
  color: #ff9800;
  font-weight: bold;
  font-size: 10pt;
}

.print-document[dir="rtl"] .flight-line::after {
  content: '←';
  left: 0;
  right: auto;
}

.flight-no-label {
  font-size: 7.5pt;
  color: #999;
  font-weight: 600;
  margin-top: 4px;
}

.flight-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #f0f0f0;
  padding-top: 8px;
  font-size: 8pt;
  color: #777;
  font-weight: 600;
}

.hotel-accommodation-grid {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 10px;
}

.hotel-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  display: flex;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.hotel-card-image-container {
  width: 200px;
  min-width: 200px;
  position: relative;
  background: #f0f0f0;
}

.hotel-card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hotel-card-content {
  padding: 15px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.hotel-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.hotel-card-title-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.hotel-card-city-badge {
  align-self: flex-start;
  background: #1976d2;
  color: #fff;
  font-size: 7.5pt;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
}

.hotel-card-name {
  font-size: 12pt;
  font-weight: 800;
  color: #111;
  margin: 0;
}

.hotel-card-stars {
  color: #ffb400;
  font-size: 10pt;
}

.hotel-card-dates {
  display: flex;
  gap: 15px;
  margin-bottom: 12px;
}

.date-box {
  background: #f8f9fa;
  border-radius: 4px;
  padding: 6px 12px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.date-box-label {
  font-size: 7pt;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
}

.date-box-val {
  font-size: 9pt;
  font-weight: 700;
  color: #333;
}

.hotel-card-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 8pt;
  color: #555;
}

.hotel-detail-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.hotel-detail-icon {
  color: #1976d2;
  font-weight: bold;
}

.hotel-nights-badge {
  align-self: flex-start;
  background: #e3f2fd;
  color: #1976d2;
  font-size: 7.5pt;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  margin-top: 8px;
}

@media print {
  .flight-card,
  .hotel-card {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }
}
`;
